import React, { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import {
  DateRangePicker,
  DesktopDateRangePicker,
  MobileDateRangePicker,
  StaticDateRangePicker,
  MultiInputDateRangeField,
  SingleInputDateRangeField
} from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import { replaceKeywordsInText } from "../../component/Charts/dashboard.utils";

export default function DateRangeComponent({
  data,
  textValue,
  setTextValue,
  formaction
}) {
  const comp = data?.component;
  const sName = comp?.sName;
  const options = comp?.options;
  const dataConfig = data?.data;
  const bPayload = dataConfig?.bPayload;
  const sendFormat = dataConfig?.sSendFormat || comp?.sSendFormat;

  useEffect(() =>{
    console.log("daterangedata",data)
  },[data])

  const dateformat = (date, sourceFormat = "YYYY-MM-DD", returnFormat) => {
    if (date) {
      const parseDate = (date, format) => {
        const parts = String(date)?.split("-");
        const formatParts = format?.toLowerCase()?.split("-");
        const dateObj = {};
        formatParts?.forEach((part, index) => {
          dateObj[part] = parseInt(parts[index], 10);
        });
        return new Date(dateObj.yyyy, dateObj.mm - 1, dateObj.dd);
      };

      const formatDate = (dateObj, format) => {
        const dd = String(dateObj.getDate()).padStart(2, "0");
        const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
        const yyyy = dateObj.getFullYear();
        switch (format) {
          case "dd-mm-yyyy":
            return `${dd}-${mm}-${yyyy}`;
          case "yyyy-dd-mm":
            return `${yyyy}-${dd}-${mm}`;
          case "yyyy-mm-dd":
            return `${yyyy}-${mm}-${dd}`;
          case "mm-dd-yyyy":
            return `${mm}-${dd}-${yyyy}`;
          default:
            return "Invalid date format pattern";
        }
      };

      const parsedDate = parseDate(date, sourceFormat?.toLowerCase());
      if (isNaN(parsedDate)) {
        return "Invalid date";
      }
      return formatDate(parsedDate, returnFormat?.toLowerCase());
    } else {
      return "";
    }
  };

  // Parse options safely — others2 and others3 may be strings, not objects
  const parseOption = (val) => {
    if (!val) return {};
    if (typeof val === "object") return val;
    return {};
  };

  // Resolve date keywords like :[startofmonth]:, :[today]:, etc.
  const resolveDate = (val) => {
    if (!val) return null;
    const resolved = replaceKeywordsInText(String(val));
    return resolved === "Invalid keyword" ? val : resolved;
  };

  const initialStart = resolveDate(comp?.sStartDefault) || resolveDate(comp?.sStartValue) || null;
  const initialEnd = resolveDate(comp?.sEndDefault) || resolveDate(comp?.sEndValue) || null;

  const [rangeValue, setRangeValue] = useState([
    initialStart ? dayjs(initialStart) : null,
    initialEnd ? dayjs(initialEnd) : null
  ]);

  // Helper to set textValue based on bPayload
  const updateTextValue = (startVal, endVal) => {
    if (bPayload) {
      // Use custom field names from data config
      const startField = dataConfig?.sStartValueFieldName;
      const endField = dataConfig?.sEndValueFieldName;
      setTextValue(prev => ({
        ...prev,
        ...(startField ? { [startField]: startVal } : {}),
        ...(endField ? { [endField]: endVal } : {}),
        [sName]: {
          sStartValue: startVal,
          sEndValue: endVal
        }
      }));
    } else {
      setTextValue(prev => ({
        ...prev,
        [sName]: {
          sStartValue: startVal,
          sEndValue: endVal
        }
      }));
    }
  };

  // Set initial textValue on mount
  useEffect(() => {
    if (sName) {
      const startVal = initialStart
        ? dateformat(initialStart, "YYYY-MM-DD", sendFormat)
        : "";
      const endVal = initialEnd
        ? dateformat(initialEnd, "YYYY-MM-DD", sendFormat)
        : "";
      updateTextValue(startVal, endVal);
    }
  }, []);

  const formatDayjs = (d) => {
    if (!d || !d.$y) return "";
    return dateformat(
      `${d.$y}-${String(d.$M + 1).padStart(2, "0")}-${String(d.$D).padStart(2, "0")}`,
      "YYYY-MM-DD",
      sendFormat
    );
  };

  const handleChange = (newValue) => {
    setRangeValue(newValue);
    const [start, end] = newValue;
    updateTextValue(formatDayjs(start), formatDayjs(end));
  };

  // Build props safely — only spread objects, not strings
  const formatProps = parseOption(options?.others1);
  const sProps = parseOption(comp?.sProps);

  // Extract known props from sProps — rest goes to slotProps.textField
  const { calendar, size, disabled, readOnly, ...restSProps } = sProps;
  const calendarsProps = calendar ? { calendars: calendar } : {};

  const commonProps = {
    value: rangeValue,
    onChange: handleChange,
    format: formatProps?.format,
    ...calendarsProps,
    disabled: disabled || false,
    readOnly: readOnly || false,
    slots: {
      field: SingleInputDateRangeField
    },
    slotProps: {
      textField: {
        helperText: comp?.sHelper,
        id: sName,
        size: size || "small",
        ...restSProps
      },
      popper: {
        disablePortal: false,
        placement: "bottom-start",
        style: { zIndex: 1300 }
      }
    }
  };

  // Shortcut presets for EnableShortCut
  const shortcutsItems = comp?.EnableShortCut
    ? [
        { label: "This Week", getValue: () => {
            const today = dayjs();
            return [today.startOf("week"), today.endOf("week")];
          }
        },
        { label: "Last 7 Days", getValue: () => {
            const today = dayjs();
            return [today.subtract(7, "day"), today];
          }
        },
        { label: "This Month", getValue: () => {
            const today = dayjs();
            return [today.startOf("month"), today.endOf("month")];
          }
        },
        { label: "Last Month", getValue: () => {
            const today = dayjs();
            return [today.subtract(1, "month").startOf("month"), today.subtract(1, "month").endOf("month")];
          }
        },
        { label: "Reset", getValue: () => [null, null] }
      ]
    : undefined;

  if (shortcutsItems) {
    commonProps.slotProps.shortcuts = { items: shortcutsItems };
  }

  const renderPicker = () => {
    const submode = options?.submode?.trim();

    // MultiDateRange uses DateRangePicker with MultiInputDateRangeField as the field slot
    if (comp?.MultiDateRange) {
      const multiProps = {
        ...commonProps,
        slots: {
          field: MultiInputDateRangeField
        },
        slotProps: {
          ...commonProps.slotProps,
          textField: {
            helperText: comp?.sHelper,
            id: sName,
            size: size || "small",
            ...restSProps
          }
        }
      };

      switch (submode) {
        case "DesktopDateRangePicker":
          return <DesktopDateRangePicker {...multiProps} />;
        case "MobileDateRangePicker":
          return <MobileDateRangePicker {...multiProps} />;
        case "StaticDateRangePicker":
          return <StaticDateRangePicker {...multiProps} />;
        case "DateRangePicker":
        default:
          return <DateRangePicker {...multiProps} />;
      }
    }

    switch (submode) {
      case "DesktopDateRangePicker":
        return <DesktopDateRangePicker {...commonProps} />;
      case "MobileDateRangePicker":
        return <MobileDateRangePicker {...commonProps} />;
      case "StaticDateRangePicker":
        return <StaticDateRangePicker {...commonProps} />;
      case "DateRangePicker":
      default:
        return <DateRangePicker {...commonProps} />;
    }
  };

  console.log("DateRangeComponent grid_props:", data?.grid_props);
  console.log("DateRangeComponent comp:", comp);
  console.log("DateRangeComponent sProps:", comp?.sProps);
  console.log("DateRangeComponent options:", options);

  return (
    <Grid item {...data?.grid_props}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {renderPicker()}
      </LocalizationProvider>
    </Grid>
  );
}
